import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { createTciSession, updateTciSession } from "@/lib/api/tci-sessions";
import { getOrCreateAnonymousId } from "@/lib/visitor-id";
import {
  VISITOR_SESSION_STORAGE_KEY,
  VisitorTrackingContext,
  type TrackVisitorArgs,
} from "@/providers/visitor-tracking-context";

function readStoredSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(VISITOR_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredSessionId(id: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (!id) {
      window.sessionStorage.removeItem(VISITOR_SESSION_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(VISITOR_SESSION_STORAGE_KEY, id);
  } catch {
    // Ignore quota / private mode failures.
  }
}

function readUtmParams() {
  if (typeof window === "undefined") {
    return {
      utmSource: undefined as string | undefined,
      utmMedium: undefined as string | undefined,
      utmCampaign: undefined as string | undefined,
      utmContent: undefined as string | undefined,
      utmTerm: undefined as string | undefined,
    };
  }
  const params = new URLSearchParams(window.location.search);
  const pick = (key: string) => params.get(key)?.trim() || undefined;
  return {
    utmSource: pick("utm_source"),
    utmMedium: pick("utm_medium"),
    utmCampaign: pick("utm_campaign"),
    utmContent: pick("utm_content"),
    utmTerm: pick("utm_term"),
  };
}

export function VisitorTrackingProvider({ children }: { children: ReactNode }) {
  const [visitorSessionId, setVisitorSessionId] = useState<string | null>(() =>
    readStoredSessionId(),
  );
  const [ready, setReady] = useState(false);
  const sessionIdRef = useRef<string | null>(visitorSessionId);
  const queueRef = useRef<TrackVisitorArgs[]>([]);
  const flushingRef = useRef(false);

  useEffect(() => {
    sessionIdRef.current = visitorSessionId;
  }, [visitorSessionId]);

  const flushQueue = useCallback(async (sessionId: string) => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const next = queueRef.current.shift();
        if (!next) break;
        try {
          await updateTciSession(sessionId, next);
        } catch {
          // Drop failed events; tracking must not block the calculator.
        }
      }
    } finally {
      flushingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function ensureSession() {
      const existing = readStoredSessionId();
      if (existing) {
        if (!cancelled) {
          setVisitorSessionId(existing);
          sessionIdRef.current = existing;
          setReady(true);
          void flushQueue(existing);
        }
        return;
      }

      try {
        const utm = readUtmParams();
        const created = await createTciSession({
          anonymousId: getOrCreateAnonymousId(),
          landingPath: `${window.location.pathname}${window.location.search}`,
          referrer: document.referrer || undefined,
          userAgent: navigator.userAgent.slice(0, 500),
          ...utm,
        });
        if (cancelled) return;
        writeStoredSessionId(created.id);
        setVisitorSessionId(created.id);
        sessionIdRef.current = created.id;
        setReady(true);
        void flushQueue(created.id);
      } catch {
        if (!cancelled) setReady(true);
      }
    }

    void ensureSession();
    return () => {
      cancelled = true;
    };
  }, [flushQueue]);

  const track = useCallback((args: TrackVisitorArgs) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) {
      queueRef.current.push(args);
      return;
    }
    void updateTciSession(sessionId, args).catch(() => {
      // Ignore tracking failures.
    });
  }, []);

  const value = useMemo(
    () => ({
      visitorSessionId,
      ready,
      track,
    }),
    [visitorSessionId, ready, track],
  );

  return (
    <VisitorTrackingContext.Provider value={value}>
      {children}
    </VisitorTrackingContext.Provider>
  );
}
